import { useState, useEffect } from "react";
import { T } from "react-translator-component";
import DoneIcon from '@mui/icons-material/Done';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Button, Typography } from "@mui/material";
import { colors } from "../common/colors";
import { PostImageItem } from "../util/types";
import SampleImage from "./SampleImage";

type SampleImageInfo = {
  id: number,
  file?: File,
  isSelected: boolean,
};

interface Props {
  sampleImages: any[];
  attachedImages: PostImageItem[];
  onChange: (images: PostImageItem[]) => void;
  limit: number;
}

function SelectSampleImages({ sampleImages, attachedImages, onChange, limit }: Props) {
  const [sampleImageList, setSampleImageList] = useState<SampleImageInfo[]>([]);
  const [showSampleImageModal, setShowSampleImageModal] = useState<boolean>(false);
  // 確定をせずにダイアログを閉じる可能性もあるので一時保存用に用意
  const [addSampleImages, setAddSampleImages] = useState<PostImageItem[]>([]);
  const [removeSampleImages, setRemoveSampleImages] = useState<number[]>([]);

  useEffect(() => {
    // 投稿の新規作成/編集画面を開いたときに ローディングが走るので、そのタイミングで画像を取得する
    const images: SampleImageInfo[] = sampleImages.map((image: any) => {
      return {
        id: image.id,
        isSelected: attachedImages.some((img: PostImageItem) => img.sample_image_id === image.id)
      }
    });
    setSampleImageList(images)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (sampleImageList.length > 0) {
      resetSampleImageList()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedImages])

  const resetSampleImageList = () => {
    const images: SampleImageInfo[] = sampleImageList.map((imgInfo: SampleImageInfo) => {
      return {
        id: imgInfo.id,
        file: imgInfo.file,
        isSelected: attachedImages.some((img: PostImageItem) => img.sample_image_id === imgInfo.id)
      }
    })
    setSampleImageList(images)
  }

  // 添付上限かどうかを判別
  const isUpperLimit = () => {
    return (addSampleImages.length + attachedImages.length - removeSampleImages.length) >= limit
  }

  // ダイアログで表示されている画像を押下したときに発火
  const handleClickSampleImage = (imgInfo: SampleImageInfo) => {
    if (imgInfo.isSelected) {
      // チェックを外す
      if (attachedImages.length > 0 && attachedImages.some((img: PostImageItem) => img.sample_image_id === imgInfo.id)) {
        setRemoveSampleImages(
          [...removeSampleImages, imgInfo.id]
        )
      } else {
        setAddSampleImages(
          addSampleImages.filter((img: PostImageItem) => img.sample_image_id !== imgInfo.id)
        )
      }
    } else {
      // チェックをつける
      if (isUpperLimit()) return
      if (!imgInfo.file) return // 画像をダウンロードできていない場合

      if (attachedImages.length > 0 && attachedImages.some((img: PostImageItem) => img.sample_image_id === imgInfo.id)) {
        setRemoveSampleImages(
          removeSampleImages.filter((id: number) => id !== imgInfo.id)
        )
      } else {
        setAddSampleImages(
          [...addSampleImages, { file: imgInfo.file, sample_image_id: imgInfo.id }]
        )
      }
    }

    imgInfo.isSelected = !imgInfo.isSelected
  }

  // 確定せず、閉じるまたはダイアログ外を押下したとき
  // ダイアログを閉じるとき
  const handleCloseModal = () => {
    setAddSampleImages([])
    setRemoveSampleImages([])
    resetSampleImageList()
    setShowSampleImageModal(false)
  }

  // 「確定」を押下したとき
  const handleSelectedSampleImages = async () => {
    const keep = attachedImages.filter((img: PostImageItem) => removeSampleImages.every((id: number) => img.sample_image_id !== id))
    const additions = addSampleImages.filter((img: PostImageItem) => attachedImages.every((orgImg: PostImageItem) => img.sample_image_id !== orgImg.sample_image_id))
    onChange([...keep, ...additions].map((img: PostImageItem) => ({ sample_image_id: img.sample_image_id, file: img.file})))
    handleCloseModal()
  }

  // 各画像右上のアイコンのstyle
  const checkIconStyle = (imgInfo: SampleImageInfo) => {
    if (imgInfo.isSelected) {
      return styles.isSelectedCheckIcon
    } else {
      if (isUpperLimit()) {
        return styles.disabledCheckIcon
      } else {
        return styles.checkIcon
      }
    }
  }

  return (
    <>
      <Button
        color={"secondary"}
        style={styles.selectSampleBtn}
        component="label"
        variant="outlined"
        onClick={() => setShowSampleImageModal(true)}
      >
        {T("select_samples")}
      </Button>

      {showSampleImageModal && (
        <div style={styles.modalBackground} onClick={() => handleCloseModal()}>
          <div style={styles.modalContainer} onClick={(e: any) => e.stopPropagation()}>
            <div style={styles.container}>
              <div
                onClick={() => handleCloseModal()}
                style={styles.closeButton}
              >
                <Typography
                  sx={styles.closeText}
                  variant="body2"
                  color="textPrimary"
                >
                  {T("close")}
                </Typography>
                <KeyboardArrowDownIcon />
              </div>
              <div>
                <Button
                  color={"secondary"}
                  style={styles.onSelectBtn}
                  component="label"
                  variant="outlined"
                  onClick={() => handleSelectedSampleImages()}
                >
                  {T("confirm")}
                </Button>
              </div>

              <div style={styles.sampleImagesContainer}>
                {sampleImageList.map((imgInfo: SampleImageInfo, index: number) => (
                  <div style={styles.imageContainer} key={index} onClick={() => handleClickSampleImage(imgInfo)}>
                    <DoneIcon style={checkIconStyle(imgInfo)} />
                    <SampleImage
                      file={imgInfo.file}
                      id={imgInfo.id}
                      style={styles.sampleImage}
                      onImageLoaded={(file) => imgInfo.file = file}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SelectSampleImages;

const styles = {
  selectSampleBtn: {
    width: 160,
    borderRadius: 20,
    borderWidth: 2,
    fontFamily: "mplus-1c-regular",
    marginTop: 5,
    color: colors.farm_black,
    backgroundColor: colors.farm_white,
  },

  closeButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: 0,
    backgroundColor: colors.farm_white,
  },

  closeText: {
    fontSize: 12,
    color: colors.farm_gray200,
  },

  container: {
    height: '100%',
    marginTop: '7px',
    paddingTop: 0,
    borderRadius: '8px',
    background: '#fff',
    overflow: 'hidden',
    border: '2 sol',
    textAlign: 'end' as 'end',
    display: 'flex',
    flexDirection: 'column' as 'column',
  },

  modalBackground: {
    position: 'fixed' as 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    zIndex: 9999,
  },

  modalContainer: {
    background: 'white',
    borderRadius: '8px',
    width: '100%',
    height: '85%',
    zIndex: 9999,
    overflowY: 'hidden' as 'hidden',
  },

  sampleImagesContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexWrap: 'wrap' as 'wrap',
    overflowY: 'scroll' as 'scroll',
    backgroundColor: 'gray',
  },

  imageContainer: {
    width: '50%',
    padding: '2px',
    position: 'relative' as 'relative',
    top: 0,
    right: 0,
    boxSizing: 'border-box' as 'border-box',
  },

  checkIcon: {
    position: 'absolute' as 'absolute',
    top: 2,
    right: 2,
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '1.5px solid gray',
    backgroundColor: 'white',
  },

  disabledCheckIcon: {
    position: 'absolute' as 'absolute',
    top: 2,
    right: 2,
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '1.5px solid gray',
    backgroundColor: 'gray',
  },

  isSelectedCheckIcon: {
    position: 'absolute' as 'absolute',
    top: 2,
    right: 2,
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '1.5px solid gray',
    backgroundColor: 'lightgreen',
  },

  sampleImage: {
    width: '100%',
    minHeight: '100px'
  },

  onSelectBtn: {
    width: "80px",
    height: "40px",
    lineHeight: "40px",
    fontSize: 18,
    color: 'red',
    textAlign: 'center' as 'center',
    border: "3px solid red",
    borderRadius: "5px",
    margin: '10px 20px'
  }
}